import { useState } from "react"
import { api, errorText } from "../api/client"
import { loadMe, openProject, openSettings } from "../store/actions"
import { getState, setUi, useStore } from "../store/store"
import { parseRepo } from "./SettingsDrawer"
import { useDismiss } from "./ui"

/**
 * Creates a project: a name and, optionally, the GitHub repo it works in.
 * Every project lives in a workspace the website never shows; the first one a
 * person makes creates theirs.
 */
async function createProject(name: string, repoText: string): Promise<string> {
	const me = getState().me
	const mine = me?.teams.find(t => t.role === "owner" || t.role === "admin")
	const projectId = mine
		? (await api.createProject(mine.id, name, { access: "restricted" })).projectId
		: (
				await api.createTeam(
					`${me?.user.name || me?.user.handle || "My"}'s projects`,
					name,
					"restricted",
				)
			).projectId!
	const repo = parseRepo(repoText)
	if (repo) {
		await api.patchProject(projectId, { githubRepos: [repo] })
		// Best effort: an online computer with `gh` creates the webhook now; the
		// settings page shows the result, or the manual steps, either way.
		await api.githubSetup(projectId, repo).catch(() => undefined)
	}
	await loadMe()
	await openProject(projectId)
	return projectId
}

export function ProjectForm({ onCancel }: { onCancel?: () => void }) {
	const [name, setName] = useState("")
	const [repo, setRepo] = useState("")
	const [busy, setBusy] = useState(false)
	const [err, setErr] = useState<string | null>(null)
	const repoOk = !repo.trim() || !!parseRepo(repo)

	const submit = async () => {
		setBusy(true)
		setErr(null)
		try {
			await createProject(name.trim(), repo)
			setUi({ newProject: false })
			// With a repo, show how its GitHub connection went; without one, the
			// sidebar's "Connect a GitHub repo" is the next step.
			if (parseRepo(repo)) openSettings("github")
		} catch (e) {
			setErr(errorText(e))
			setBusy(false)
		}
	}

	return (
		<form
			className="dialog-body"
			onSubmit={e => {
				e.preventDefault()
				void submit()
			}}
		>
			<label className="field">
				Project name
				<input
					className="input"
					required
					autoFocus
					maxLength={80}
					placeholder="e.g. checkout-v2"
					value={name}
					onChange={e => setName(e.target.value)}
				/>
			</label>
			<label className="field">
				<span>
					GitHub repo <span className="dim">(optional, you can connect it later)</span>
				</span>
				<input
					className="input mono"
					placeholder="owner/repo or https://github.com/owner/repo"
					value={repo}
					onChange={e => setRepo(e.target.value)}
					aria-invalid={!repoOk}
				/>
			</label>
			{err && <div className="form-error">{err}</div>}
			<div className="dialog-actions">
				{onCancel && (
					<button type="button" className="btn" onClick={onCancel}>
						Cancel
					</button>
				)}
				<button className="btn btn-primary" disabled={busy || !name.trim() || !repoOk}>
					Create project
				</button>
			</div>
		</form>
	)
}

export function NewProjectDialog() {
	const close = () => setUi({ newProject: false })
	useDismiss(close)
	return (
		<>
			<div className="scrim" onMouseDown={close} />
			<div className="dialog" role="dialog" aria-label="New project">
				<div className="dialog-h">
					New project
					<button type="button" className="icon-btn" aria-label="Close" onClick={close}>
						×
					</button>
				</div>
				<ProjectForm onCancel={close} />
			</div>
		</>
	)
}

/** The landing screen for someone with no project yet. */
export function FirstProject() {
	const name = useStore(s => s.me?.user.name)
	return (
		<div className="firstrun">
			<div className="firstrun-card">
				<h1>{name ? `Welcome, ${name.split(" ")[0]}.` : "Welcome."} Start a project.</h1>
				<p className="explain">
					A project is a shared space for one piece of work. It has <b>rooms</b> to talk
					in, one <b>GitHub repo</b>, the <b>people</b> you invite, and everyone's Claude
					and Codex chats.
				</p>
				<ProjectForm />
				<p className="dim" style={{ fontSize: 11, margin: 0 }}>
					Joining someone else's project? Open the invitation link they sent you.
				</p>
			</div>
		</div>
	)
}
